import {
  invoiceActions,
  orderActions,
  orderCardActions,
  selectOrderCardState,
  selectOrderState,
  StorageService,
} from '@aa/angular/core';
import { isCardLevelChangeFlow } from '@aa/ts/common';
import {
  CardFormModel,
  reduceFieldsToKeys,
  cardFormArrayFields,
  getCardFormFieldsAndLoadOptions,
} from '@aa/angular/customer';
import {
  OrderCardResourceTypeMappings,
  UpdateOrderCardDTO,
} from '@aa/nest/resource';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  signal,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import {
  BehaviorSubject,
  combineLatestWith,
  debounceTime,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  Subscription,
  tap,
} from 'rxjs';
import { StaffAppState } from '../../state/index.reducers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MultipleRequestedServicesFormFieldValue,
  RequestedServicesFormFieldValue,
  SingleRequestedServicesFormFieldValue,
} from '../requested-services-form-field/requested-services-form-field.component';
import { MatTabsModule } from '@angular/material/tabs';
import { ChatComponent } from '../../../../../core/src/lib/components/chat/chat.component';
import { MatDialog } from '@angular/material/dialog';

const flatLevelAttributes = [
  'centeringGrade',
  'cornersGrade',
  'surfaceGrade',
  'edgesGrade',
  'grade',
  'value',
  'requestedGradingCompany',
  'minimumGradeForSubmission',
  'customerFacingNotes',
];

export type StaffCardFormModel = Omit<CardFormModel, 'requestedServices'> & {
  requestedServices: RequestedServicesFormFieldValue;
  customerFacingNotes?: string;
};

@Component({
  selector: 'aa-order-cards-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyMaterialModule,
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    ChatComponent,
  ],
  templateUrl: './order-cards-tab.component.html',
  styleUrl: './order-cards-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderCardsTabComponent {
  form = new FormGroup({});
  model: StaffCardFormModel = {
    requestedServices: {
      draftValues: [],
      values: [],
    },
    cardNumber: '',
    characterName: '',
    issuedYear: '',
    notes: '',
    playerName: '',
    // pokemonType: '',
    productionCompany: '',
    serialNumber: '',
    setName: '',
    variation: '',
  };
  fields: FormlyFieldConfig[] = [];
  hideForm$ = new BehaviorSubject(false);

  orderId$: Observable<number>;
  cards$: Observable<OrderCardResourceTypeMappings['resourceWithRelationsT'][]>;
  cardsLoading$: Observable<boolean>;

  currentCard$ = new BehaviorSubject<
    OrderCardResourceTypeMappings['resourceWithRelationsT'] | null
  >(null);
  hasNextCard$: Observable<boolean>;
  hasPrevCard$: Observable<boolean>;
  currentTabIndex$ = new BehaviorSubject(0);
  aiLoading$ = new BehaviorSubject<boolean>(false);

  saving$: Observable<boolean> = this.currentCard$.pipe(
    combineLatestWith(
      this.store.select((s) => selectOrderCardState(s).cardsSaving),
    ),
    map(([card, cardsSaving]) => {
      if (card) {
        return cardsSaving[card.id] ?? false;
      }
      return false;
    }),
  );

  unsavedChanges = signal(false);

  isCardLevelChangeFlow = isCardLevelChangeFlow;
  currentOrderCreatedAt: string | null = null;

  constructor(
    private readonly store: Store<StaffAppState>,
    private readonly route: ActivatedRoute,
    private readonly storageService: StorageService,
    private readonly destroyRef: DestroyRef,
    private readonly dialog: MatDialog,
  ) {
    this.orderId$ = this.route.paramMap.pipe(
      map((paramMap) => parseInt(paramMap.get('id')!)),
      takeUntilDestroyed(),
    );

    this.cards$ = this.store
      .select((s) => selectOrderCardState(s).items)
      .pipe(takeUntilDestroyed());

    this.route.queryParamMap
      .pipe(combineLatestWith(this.cards$), takeUntilDestroyed())
      .subscribe(async ([queryMap, cards]) => {
        const threadOrderCardId = queryMap.get('threadOrderCardId');
        if (threadOrderCardId) {
          const card = cards.find((c) => c.id == +threadOrderCardId);
          if (card) {
            this.setCurrentCard(card);
            this.selectedIndexChanged(1);
          }
        }
      });

    this.cardsLoading$ = this.store
      .select((s) => selectOrderCardState(s).itemsLoading)
      .pipe(takeUntilDestroyed());

    this.saving$.pipe(takeUntilDestroyed()).subscribe((saving) => {
      if (!saving) {
        this.unsavedChanges.set(false);
      }
    });

    getCardFormFieldsAndLoadOptions(store, this.destroyRef, true).subscribe(
      (formFields) => {
        this.fields = [
          {
            template: '<p><strong>Provided Details</strong><p/>',
          },
          ...formFields,
        ];
      },
    );

    this.orderId$.pipe(
      combineLatestWith(
        this.store.select((s) => selectOrderState(s).items),
      ),
      takeUntilDestroyed(),
    ).subscribe(([orderId, orders]) => {
      const order = orders.find((o) => o.id === orderId);
      this.currentOrderCreatedAt = order?.createdAt
        ? order.createdAt.toString()
        : null;
    });

    this.orderId$.subscribe((orderId) => {
      this.store.dispatch(
        orderCardActions.loadItems({
          query: {
            where: {
              orderId,
            },
            include: {
              orderCardServiceLinkages: {
                include: {
                  invoiceItem: true,
                  refundItem: true,
                  offeredService: {
                    include: {
                      parentOfferedService: true,
                    },
                  },
                },
              },
              orderCardAttributes: {
                include: {
                  orderCardAttributeType: true,
                },
              },
              threads: {
                include: {
                  messages: {
                    where: { messageType: 'change_approval_request' },
                    orderBy: { sentAt: 'desc' },
                    take: 1,
                  },
                },
              },
            },
          },
        }),
      );
    });

    this.hasNextCard$ = this.currentCard$.pipe(
      combineLatestWith(this.cards$),
      map(([current, cards]) => {
        if (current) {
          const currentCardIndex = cards.findIndex((c) => c.id === current.id);
          if (cards.length - 1 > currentCardIndex) {
            return true;
          }
        }

        return false;
      }),
    );
    this.hasPrevCard$ = this.currentCard$.pipe(
      combineLatestWith(this.cards$),
      map(([current, cards]) => {
        if (current) {
          const currentCardIndex = cards.findIndex((c) => c.id === current.id);

          if (currentCardIndex > 0) {
            return true;
          }
        }

        return false;
      }),
    );

    let autoSaveSubscription: Subscription;
    this.currentCard$?.subscribe((card) => {
      const tradingCardType = card?.orderCardAttributes?.find(
        (ca) => ca.orderCardAttributeType?.fieldName == 'tradingCardType',
      )?.value;

      const patchValue: Record<string, any> = {
        // ...this.model,
        tradingCardType,
        requestedServices: {
          draftValues: [
            ...(card?.orderCardServiceLinkages
              ?.filter(
                (li) =>
                  !li.offeredService?.parentOfferedServiceId && li.isDraft,
              )
              ?.map((sl) => sl.offeredServiceId) ?? []),
          ],
          values: [
            ...(card?.orderCardServiceLinkages
              ?.filter(
                (li) =>
                  !li.offeredService?.parentOfferedServiceId && !li.isDraft,
              )
              ?.map((sl) => sl.offeredServiceId) ?? []),
          ],
          customPrices: card?.orderCardServiceLinkages
            ?.filter(
              (li) =>
                !li.offeredService?.parentOfferedServiceId &&
                li.isDraft &&
                li.customPrice != null,
            )
            ?.map((sl) => ({
              offeredServiceId: sl.offeredServiceId,
              price: parseFloat(sl.customPrice as any),
            })) ?? [],
        },
        ...flatLevelAttributes.reduce(
          (obj, curr) => ({
            ...obj,
            [curr]:
              card?.[
                curr as keyof OrderCardResourceTypeMappings['resourceWithRelationsT']
              ],
          }),
          {} as Record<string, any>,
        ),
        ...card?.orderCardAttributes
          ?.filter((attribute) => attribute.value)
          ?.reduce(
            (obj, attribute) => ({
              ...obj,
              [attribute.orderCardAttributeType?.fieldName]:
                cardFormArrayFields.includes(
                  attribute.orderCardAttributeType?.fieldName,
                ) &&
                attribute.value &&
                attribute.value.includes('[') &&
                attribute.value.includes(']')
                  ? JSON.parse(attribute.value)
                  : attribute.value,
            }),
            {} as CardFormModel,
          ),

        value: card?.value as any,
        customerFacingNotes: card?.customerFacingNotes as any,
      };

      for (const serviceLinkage of card?.orderCardServiceLinkages ?? []) {
        const service = serviceLinkage.offeredService;
        if (service.parentOfferedService) {
          const key = `${(
            service.parentOfferedService?.abbreviation ??
            service.parentOfferedService?.name
          )
            .toLowerCase()
            .replaceAll(' ', '')}${service.group?.replaceAll(' ', '')}Addon`;

          if (patchValue[key]) {
            if (serviceLinkage.isDraft && !patchValue[key].draftValue) {
              patchValue[key] = { ...patchValue[key], draftValue: service.id };
            } else if (!serviceLinkage.isDraft && !patchValue[key].value) {
              patchValue[key] = { ...patchValue[key], value: service.id };
            } else if (
              patchValue[key]?.values ||
              patchValue[key]?.draftValues
            ) {
              const v: MultipleRequestedServicesFormFieldValue =
                patchValue[key];

              if (serviceLinkage.isDraft) {
                patchValue[key] = {
                  ...v,
                  draftValues: [...v.draftValues, service.id],
                };
              } else {
                patchValue[key] = {
                  ...v,
                  values: [...v.values, service.id],
                };
              }
            } else {
              const v: SingleRequestedServicesFormFieldValue = patchValue[key];

              if (serviceLinkage.isDraft) {
                patchValue[key] = {
                  draftValues: v.draftValue
                    ? [v.draftValue, service.id]
                    : [service.id],
                  values: v.value ? [v.value] : [],
                };
              } else {
                patchValue[key] = {
                  draftValues: v.draftValue ? [v.draftValue] : [],
                  values: v.value ? [v.value, service.id] : [service.id],
                };
              }
            }
          } else {
            if (serviceLinkage.isDraft) {
              patchValue[key] = {
                draftValue: service.id,
              };
            } else {
              patchValue[key] = {
                value: service.id,
              };
            }
          }
        }
      }

      this.model = { ...(patchValue as any) };

      if (autoSaveSubscription) {
        autoSaveSubscription.unsubscribe();
      }
      autoSaveSubscription = this.form.valueChanges
        .pipe(
          tap(() => {
            if (this.form.dirty) {
              this.unsavedChanges.set(true);
            }
          }),
          debounceTime(1000),
        )
        .subscribe(() => {
          if (this.form.dirty) this.save();
        });
    });

    this.cards$.subscribe(async (cards) => {
      const currentCard = await firstValueFrom(this.currentCard$);

      if (!cards.find((c) => c.id == currentCard?.id)) {
        this.setCurrentCard(null);
      }
    });
  }

  async setCurrentCard(
    orderCard: OrderCardResourceTypeMappings['resourceWithRelationsT'] | null,
  ) {
    this.form.reset();
    this.hideForm$.next(true);
    if (orderCard != null) {
      const copyOfOrderCard = structuredClone(orderCard);
      copyOfOrderCard.frontImageURL = (
        await lastValueFrom(
          this.storageService.getFreshURL(
            copyOfOrderCard.frontImageStorageSlug ?? '',
          ),
        )
      ).url;
      copyOfOrderCard.backImageURL = (
        await lastValueFrom(
          this.storageService.getFreshURL(
            copyOfOrderCard.backImageStorageSlug ?? '',
          ),
        )
      ).url;
      this.currentCard$.next(copyOfOrderCard);
    } else {
      this.currentCard$.next(orderCard);
    }
    setTimeout(() => {
      this.hideForm$.next(false);
    }, 100);
    setTimeout(() => {
      document.querySelector('#pagination')?.scrollIntoView({
        behavior: 'smooth', // smooth scroll
        block: 'start', // the upper border of the element will be aligned at the top of the visible part of the window of the scrollable area.
      });
    }, 101);
  }

  async handleImageClick(isBackImage = false) {
    const currentCard = await firstValueFrom(this.currentCard$);
    if (currentCard) {
      const newSrc = (
        await lastValueFrom(
          this.storageService.getFreshURL(
            isBackImage
              ? currentCard.backImageStorageSlug!
              : currentCard.frontImageStorageSlug!,
          ),
        )
      )?.url;
      this.enlargePhoto(newSrc);
    }
  }

  enlargePhoto(src: string) {
    const img = new Image();
    img.src = src;
    // const photoViewer = new PhotoViewer([img], {
    //   title: false,
    // });
  }

  selectedIndexChanged(index: number) {
    this.currentTabIndex$.next(index);
  }

  cancel() {
    this.setCurrentCard(null);
  }

  async saveAndSend() {
    await this.save();
    // await this.adjustInvoicingForChanges();
    await this.toggleShowChangesToCustomer();
    await this.sendChangeEmail();
  }

  async save() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const card = await firstValueFrom(this.currentCard$);
    const cardId = card?.id;
    const currentOrderCardAttributes = card?.orderCardAttributes;

    let draftRequestedServices = [
      ...(
        this.model.requestedServices as MultipleRequestedServicesFormFieldValue
      ).draftValues,
    ];
    let actualRequestedServices = [
      ...(
        this.model.requestedServices as MultipleRequestedServicesFormFieldValue
      ).values,
    ];
    const customPrices = [
      ...((this.model.requestedServices as MultipleRequestedServicesFormFieldValue)
        .customPrices ?? []),
    ];

    // let requestedServices = [
    //   ...Object.entries(this.model.requestedServices)
    //     .filter(([key, value]) => value == true)
    //     .map(([key, value]) => parseInt(key)),
    // ];
    for (const [key, value] of Object.entries(this.model)) {
      if (key.substring(key.length - 5) == 'Addon') {
        if ((value as any).values) {
          actualRequestedServices = [
            ...actualRequestedServices,
            ...(value as MultipleRequestedServicesFormFieldValue).values,
          ];
          draftRequestedServices = [
            ...draftRequestedServices,
            ...(value as MultipleRequestedServicesFormFieldValue).draftValues,
          ];
        } else {
          if ((value as SingleRequestedServicesFormFieldValue).value)
            actualRequestedServices = [
              ...actualRequestedServices,
              (value as SingleRequestedServicesFormFieldValue).value,
            ];
          if ((value as SingleRequestedServicesFormFieldValue).draftValue)
            draftRequestedServices = [
              ...draftRequestedServices,
              (value as SingleRequestedServicesFormFieldValue).draftValue,
            ];
        }
      }
    }

    const model: any = { ...this.form.value };
    const attributeKeys = reduceFieldsToKeys(this.fields);

    let generatedCardLabel = (
      ['pokemon'].includes(model.tradingCardType!)
        ? `${model.characterName ?? ''} ${model.issuedYear ?? ''} ${
            model.setName ?? ''
          } ${model.cardNumber ? `#${model.cardNumber}` : ''}`
        : `${model.playerName} ${model.issuedYear} ${model.issue ?? ''} ${
            model.cardNumber ? `#${model.cardNumber}` : ''
          } ${model.serialNumber ?? ''}`
    )?.replaceAll('  ', ' ');
    if (generatedCardLabel.endsWith(' ')) {
      generatedCardLabel = generatedCardLabel.substring(
        0,
        generatedCardLabel.length - 1,
      );
    }

    if (cardId) {
      this.store.dispatch(
        orderCardActions.updateCardWithAttributes({
          id: cardId,
          cardUpdates: {
            ...(Object.entries(model)
              .filter(([key, value]) => flatLevelAttributes.includes(key))
              .reduce(
                (obj, [key, value]) => ({
                  ...obj,
                  [key]: value,
                }),
                {} as UpdateOrderCardDTO,
              ) ?? {}),
            label: generatedCardLabel,
          },
          offeredServicesIds: actualRequestedServices,
          draftOfferedServicesIds: draftRequestedServices,
          attributes: attributeKeys
            .filter(
              (key) =>
                key !== 'requestedServices' &&
                key.substring(key.length - 5) != 'Addon' &&
                !flatLevelAttributes.includes(key),
            )
            .map((key) => ({
              orderCardId: cardId,
              orderCardAttributeTypeId: currentOrderCardAttributes?.find(
                (ca) => ca.orderCardAttributeType.fieldName == key,
              )?.orderCardAttributeTypeId,
              fieldName: key,
              value:
                cardFormArrayFields.includes(key) && model[key]
                  ? JSON.stringify(model[key])
                  : model[key],
            })),
          updateInvoiceForOrderId: isCardLevelChangeFlow(this.currentOrderCreatedAt)
            ? undefined
            : await firstValueFrom(this.orderId$),
          customPrices,
        }),
      );
    }
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  async toggleShowChangesToCustomer() {
    const orderId = await firstValueFrom(this.orderId$);
    this.store.dispatch(
      orderActions.updateItem({
        id: orderId,
        data: {
          showChangesToCustomer: true,
        },
      }),
    );
  }

  async sendChangeEmail() {
    // const data: ConfirmDialogData = {
    //   title:
    //     'Are you sure you want to send these drafted changes to the customer?',
    // };
    // const confirmation = await lastValueFrom(
    //   this.dialog
    //     .open(ConfirmDialogComponent, {
    //       autoFocus: false,
    //       data,
    //     })
    //     .afterClosed(),
    // );

    // if (!confirmation) return;
    const id = await firstValueFrom(this.orderId$);
    this.store.dispatch(
      orderActions.sendChangeEmail({
        id,
      }),
    );
  }

  async adjustInvoicingForChanges() {
    this.store.dispatch(
      invoiceActions.autoAdjustForOrder({
        orderId: await firstValueFrom(this.orderId$),
      }),
    );
  }

  async adjustInvoicingForCard() {
    const card = await firstValueFrom(this.currentCard$);
    if (card) {
      this.store.dispatch(
        invoiceActions.autoAdjustForOrderCard({
          orderCardId: card.id,
        }),
      );
    }
  }

  async nextCard() {
    const current = await firstValueFrom(this.currentCard$);
    const cards = await firstValueFrom(this.cards$);

    if (current) {
      const currentIndex = cards.findIndex((c) => c.id == current.id);
      const nextCard = cards[currentIndex + 1];
      if (nextCard) {
        this.setCurrentCard(nextCard);
      }
    }
  }

  async prevCard() {
    const current = await firstValueFrom(this.currentCard$);
    const cards = await firstValueFrom(this.cards$);

    if (current) {
      const currentIndex = cards.findIndex((c) => c.id == current.id);
      const prevCard = cards[currentIndex - 1];
      if (prevCard) {
        this.setCurrentCard(prevCard);
      }
    }
  }
}
